<div class="receipt-entry-page">
    <app-page-shell>
        <p-toast position="top-center" [life]="10000"></p-toast>
        <p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

        <app-page-hero eyebrow="Receivables" [title]="pageTitle"
            subtitle="Record customer money, capture payment details, and allocate receipts against outstanding invoices.">
            <div actions>
                <p-button type="button" label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true"
                    (click)="cancel()"></p-button>
                <p-button type="button" label="Save Receipt" icon="pi pi-check" [loading]="isLoading"
                    [disabled]="!canSave" (click)="saveReceiptEntry()"></p-button>
            </div>
        </app-page-hero>

        <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

        <form id="receiptEntryForm" [formGroup]="receiptEntryForm" (ngSubmit)="saveReceiptEntry()"
            class="receipt-workspace">
            <div class="receipt-main-column">
                <app-page-form-section title="Receipt Details" icon="pi pi-receipt"
                    subtitle="Banking, posting date, and payment reference information.">
                    <div class="receipt-form-grid three">
                        <div class="field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-datePicker id="postingDate" formControlName="postingDate" dateFormat="yy-mm-dd"
                                        appendTo="body" styleClass="w-full"></p-datePicker>
                                    <label for="postingDate">Posting Date</label>
                                </p-iftalabel>
                            </p-inputGroup>
                            @if (receiptEntryForm.get('postingDate')?.invalid &&
                            receiptEntryForm.get('postingDate')?.touched) {
                            <small class="p-error">Posting Date is required.</small>
                            }
                        </div>

                        <div class="field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-money-bill"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-select id="modeOfPayment" [options]="modeOfPaymentOptions"
                                        formControlName="modeOfPayment" appendTo="body"
                                        (onChange)="onModeOfPaymentChange()" styleClass="w-full"></p-select>
                                    <label for="modeOfPayment">Mode of Payment</label>
                                </p-iftalabel>
                            </p-inputGroup>
                            @if (receiptEntryForm.get('modeOfPayment')?.invalid &&
                            receiptEntryForm.get('modeOfPayment')?.touched) {
                            <small class="p-error">Mode of Payment is required.</small>
                            }
                        </div>

                        @if (!requiresBankAccount) {
                        <div class="field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-wallet"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-select id="cashAccountId" [options]="cashAccounts" formControlName="cashAccountId"
                                        optionLabel="accountName" optionValue="accountId" [filter]="true"
                                        filterBy="accountCode,accountName" [showClear]="true" appendTo="body"
                                        placeholder="Use default cash account" styleClass="w-full"></p-select>
                                    <label for="cashAccountId">Cash Ledger Account</label>
                                </p-iftalabel>
                            </p-inputGroup>
                            <small>Default: {{ accountLabel(ledgerDefaults?.cashAccountId) }}</small>
                        </div>
                        }

                        @if (requiresBankAccount) {
                        <div class="field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-building-columns"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-select id="companyBankAccount" [options]="eligibleBankAccounts"
                                        formControlName="companyBankAccount" optionLabel="accountName" appendTo="body"
                                        styleClass="w-full"></p-select>
                                    <label for="companyBankAccount">Company Bank Account</label>
                                </p-iftalabel>
                            </p-inputGroup>
                            @if (receiptEntryForm.get('companyBankAccount')?.invalid &&
                            receiptEntryForm.get('companyBankAccount')?.touched) {
                            <small class="p-error">Company Bank Account is required.</small>
                            }
                            @if (receiptEntryForm.get('companyBankAccount')?.value?.account) {
                            <small>Ledger: {{ receiptEntryForm.get('companyBankAccount')?.value?.account?.accountCode }} — {{ receiptEntryForm.get('companyBankAccount')?.value?.account?.accountName }}</small>
                            }
                        </div>

                        <div class="field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <input id="paymentTransactionNumber" pInputText
                                        formControlName="paymentTransactionNumber" />
                                    <label for="paymentTransactionNumber">Payment Trxn #</label>
                                </p-iftalabel>
                            </p-inputGroup>
                        </div>
                        }
                    </div>
                </app-page-form-section>

                <app-page-form-section title="Party and Contact" icon="pi pi-users"
                    subtitle="Choose the receiving party. CRM details are optional enrichment.">
                    <div class="receipt-form-grid two">
                        <app-commercial-party-picker class="field span-2"
                            [owningCompanyId]="commercialPartyCompanyId" [value]="commercialPartyReference" [required]="true"
                            label="Receipt Commercial Party"
                            (quickCreated)="onCrmQuickCreated($event)"
                            (valueChange)="onCommercialPartyChange($event)">
                        </app-commercial-party-picker>

                        @if (requiresBankAccount) {
                        <div class="field span-2">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-building-columns"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-select id="partyBankAccount" [options]="partyBankAccounts"
                                        formControlName="partyBankAccount" optionLabel="accountName" appendTo="body"
                                        styleClass="w-full"></p-select>
                                    <label for="partyBankAccount">Party Bank Account</label>
                                </p-iftalabel>
                                <p-button type="button" icon="pi pi-plus" severity="success"
                                    (click)="showNewBankAccountDialog()"
                                    [disabled]="!receiptEntryForm.get('party')?.value"
                                    pTooltip="Add new bank account"></p-button>
                            </p-inputGroup>
                        </div>
                        }
                    </div>
                </app-page-form-section>

                <app-page-form-section title="Amount" icon="pi pi-wallet"
                    subtitle="Receipt value, reporting currency, and linked logistics project.">
                    <div class="receipt-form-grid two">
                        <div class="field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-dollar"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-inputNumber id="paidAmount" formControlName="paidAmount" mode="decimal"
                                        [minFractionDigits]="4" [maxFractionDigits]="4" [min]="0" styleClass="w-full"
                                        (onBlur)="onPaidAmountChange()"></p-inputNumber>
                                    <label for="paidAmount">Received Amount ({{ receiptEntryForm.get('currencyCode')?.value
                                        }})</label>
                                </p-iftalabel>
                            </p-inputGroup>
                            @if (receiptEntryForm.get('paidAmount')?.invalid &&
                            receiptEntryForm.get('paidAmount')?.touched) {
                            <small class="p-error">
                                @if (receiptEntryForm.get('paidAmount')?.hasError('required')) {
                                Received Amount is required.
                                } @else if (receiptEntryForm.get('paidAmount')?.hasError('min')) {
                                Amount must be positive.
                                }
                            </small>
                            }
                        </div>

                        <div class="field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-money-bill"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <input id="currencyCode" type="text" pInputText formControlName="currencyCode"
                                        readonly class="p-disabled" />
                                    <label for="currencyCode">Currency</label>
                                </p-iftalabel>
                            </p-inputGroup>
                        </div>

                        <div class="field span-2">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-book"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-select id="counterpartAccountId" [options]="counterpartAccounts"
                                        formControlName="counterpartAccountId" optionLabel="accountName" optionValue="accountId"
                                        [filter]="true" filterBy="accountCode,accountName" [showClear]="true"
                                        appendTo="body" placeholder="Use default receivable account" styleClass="w-full"></p-select>
                                    <label for="counterpartAccountId">Credited Receivable Account</label>
                                </p-iftalabel>
                            </p-inputGroup>
                            <small>Default credit: {{ accountLabel(ledgerDefaults?.receivableAccountId) }}</small>
                        </div>

                        <div class="field span-2">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-briefcase"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <app-logistics-project-lookup inputId="logisticsProjectId" formControlName="logisticsProjectId"></app-logistics-project-lookup>
                                    <label for="logisticsProjectId">Logistics Project</label>
                                </p-iftalabel>
                            </p-inputGroup>
                        </div>
                    </div>
                </app-page-form-section>
            </div>

            <aside class="receipt-side-column">
                <app-page-data-panel title="Allocation Summary" [resultText]="formatMoney(unallocatedAmount)">
                    <div class="allocation-summary">
                        <div>
                            <span>Customer</span>
                            <strong>{{ selectedPartyName }}</strong>
                        </div>
                        <div>
                            <span>Total allocated</span>
                            <strong>{{ formatMoney(totalAllocatedAmount) }}</strong>
                        </div>
                        <div>
                            <span>Unallocated amount</span>
                            <strong [class.warn]="unallocatedAmount > 0">{{ formatMoney(unallocatedAmount) }}</strong>
                        </div>
                        <div>
                            <span>Difference</span>
                            <strong [class.success]="differenceAmount === 0" [class.warn]="differenceAmount !== 0">
                                {{ formatMoney(differenceAmount) }}
                            </strong>
                        </div>
                    </div>
                </app-page-data-panel>

                <app-page-form-section title="Remarks" icon="pi pi-comment"
                    subtitle="Internal notes for finance review.">
                    <p-iftalabel>
                        <textarea id="remarks" pInputTextarea formControlName="remarks" rows="7"
                            class="w-full"></textarea>
                        <label for="remarks">Remarks</label>
                    </p-iftalabel>
                </app-page-form-section>
            </aside>

            <app-page-data-panel title="Reference Allocations"
                subtitle="Select outstanding invoices and set how much of this receipt should be applied."
                [resultText]="selectedReferenceCount + ' selected'">
                <div actions>
                    <p-button type="button" label="Get Outstanding Invoices" icon="pi pi-download" severity="success"
                        [outlined]="true" (click)="getOutstandingInvoices()"
                        [disabled]="!receiptEntryForm.get('party')?.value"></p-button>
                </div>

                <p-table [value]="outstandingReferences" styleClass="receipt-allocation-table p-datatable-sm"
                    responsiveLayout="scroll">
                    <ng-template pTemplate="header">
                        <tr>
                            <th style="width: 3rem"></th>
                            <th>Type</th>
                            <th>Reference #</th>
                            <th>Date</th>
                            <th class="text-right">Total Amount</th>
                            <th class="text-right">Outstanding</th>
                            <th class="text-right allocation-input">Amount to Allocate</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-ref>
                        <tr>
                            <td data-label="Select">
                                <p-checkbox [(ngModel)]="ref.selected" [binary]="true"
                                    [ngModelOptions]="{standalone: true}"
                                    (onChange)="onReferenceSelect(ref)"></p-checkbox>
                            </td>
                            <td data-label="Type">
                                <span class="code-pill">{{ ref.type }}</span>
                            </td>
                            <td data-label="Reference #">
                                <strong class="reference-number">{{ ref.name }}</strong>
                            </td>
                            <td data-label="Date">{{ ref.date | date:'yyyy-MM-dd' }}</td>
                            <td data-label="Total Amount" class="text-right">
                                {{ ref.grandTotal | currency: selectedCurrencyCode : (selectedCurrencyCode || 'SAR') + ' ' : '1.4-4' }}
                            </td>
                            <td data-label="Outstanding" class="text-right">
                                {{ ref.outstandingAmount | currency: selectedCurrencyCode : (selectedCurrencyCode || 'SAR') + ' ' : '1.4-4' }}
                            </td>
                            <td data-label="Amount to Allocate">
                                <p-inputNumber [(ngModel)]="ref.allocatedAmount" [disabled]="!ref.selected"
                                    mode="decimal" [minFractionDigits]="4" [maxFractionDigits]="4" [min]="0"
                                    [max]="ref.outstandingAmount" [ngModelOptions]="{standalone: true}"
                                    (onInput)="onAllocationChange(ref)"></p-inputNumber>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="7">
                                <div class="empty-state">
                                    <i class="pi pi-inbox"></i>
                                    <strong>No outstanding references found</strong>
                                    <span>Select a customer and get outstanding invoices to start allocating.</span>
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="footer">
                        <tr>
                            <td colspan="6" class="text-right font-bold">Total Allocated:</td>
                            <td class="text-right font-bold">{{ totalAllocatedAmount | currency: selectedCurrencyCode : (selectedCurrencyCode || 'SAR') + ' ' :
                                '1.4-4' }}</td>
                        </tr>
                        <tr>
                            <td colspan="6" class="text-right">Unallocated Amount:</td>
                            <td class="text-right">{{ unallocatedAmount | currency: selectedCurrencyCode : (selectedCurrencyCode || 'SAR') + ' ' : '1.4-4' }}</td>
                        </tr>
                        <tr class="font-bold"
                            [ngClass]="{'text-red-500': differenceAmount !== 0, 'text-green-500': differenceAmount === 0}">
                            <td colspan="6" class="text-right">Difference:</td>
                            <td class="text-right">{{ differenceAmount | currency: selectedCurrencyCode : (selectedCurrencyCode || 'SAR') + ' ' : '1.4-4' }}</td>
                        </tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>
        </form>
    </app-page-shell>
</div>
